/*
 * ValoraaBead product page behaviour (Figma product page). Loaded as a module by
 * sections/product-information.liquid.
 * - <vb-product-gallery>: thumbnail rail, swipe track, dots, keyboard, Zoom lightbox, and jumping
 *   to the selected variant's image (Horizon's product-select event).
 * - Buy box: heart (saved on this device), pincode format check + delivery promise, sticky add-to-bag bar.
 * - "You may also love": swaps the fallback collection for Shopify's recommendations.
 * Progressive enhancement: without JS the gallery still scrolls and the buy form still posts.
 */

import { StandardEvents } from '@shopify/events';

/**
 * Runs `fn(detail)` after each variant change on the product page. Horizon's variant picker fires
 * the standard product-select event with a promise for the re-rendered section HTML and variant.
 * @param {(detail: {html?: Document, productId?: string, resource?: any}) => void} fn
 */
function onVariantChange(fn) {
  document.addEventListener(StandardEvents.productSelect, (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    /** @type {Promise<any> | undefined} */
    const promise = /** @type {any} */ (event).promise;
    promise
      ?.then((result) => result?.detail && fn(result.detail))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[vb-product]', error);
      });
  });
}

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const behavior = () => (reduceMotion() ? 'auto' : 'smooth');

/* ---------- Gallery ---------- */
class VbProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.track = this.querySelector('[data-vb-track]');
    this.slides = [...this.querySelectorAll('[data-vb-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-vb-thumb]')];
    this.dots = [...this.querySelectorAll('[data-vb-dot]')];
    this.status = this.querySelector('[data-vb-status]');
    this.rail = this.querySelector('.vb-pdp-gallery__rail');
    this.dialog = this.querySelector('[data-vb-zoom]');
    this.index = Number(this.dataset.start) || 0;
    if (!this.track) return;

    if (this.index > 0) requestAnimationFrame(() => this.goTo(this.index, false));

    // Thumbnail rail: fade the bottom edge while more thumbs are hidden below
    if (this.rail) {
      const rail = this.rail;
      const updateRail = () => {
        rail.dataset.overflow = String(rail.scrollHeight > rail.clientHeight + 1);
        rail.dataset.atEnd = String(rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 2);
      };
      rail.addEventListener('scroll', updateRail, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(updateRail).observe(rail);
      updateRail();
    }

    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.vbThumb), true, true))
    );

    let frame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.sync());
      },
      { passive: true }
    );

    this.track.addEventListener('keydown', (event) => {
      const keys = { ArrowRight: 1, ArrowLeft: -1 };
      if (event.key in keys) {
        event.preventDefault();
        this.goTo(this.index + keys[event.key], true, true);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.goTo(event.key === 'Home' ? 0 : this.slides.length - 1, true, true);
      } else if (event.key === 'Enter' && this.dialog) {
        event.preventDefault();
        this.openZoom(this.index);
      }
    });

    this.track.addEventListener('click', (event) => {
      const img = event.target instanceof Element ? event.target.closest('[data-vb-zoomable]') : null;
      if (img && this.dialog) this.openZoom(Number(img.getAttribute('data-vb-zoomable')));
    });

    this.querySelector('[data-vb-zoom-open]')?.addEventListener('click', () => this.openZoom(this.index));
    this.bindZoom();

    onVariantChange((detail) => {
      const media = detail.resource?.featured_media;
      if (!media || !this.isConnected) return;
      if (detail.productId && String(detail.productId) !== this.dataset.productId) return;
      const i = this.slides.findIndex((slide) => slide.dataset.mediaId === String(media.id));
      if (i >= 0) this.goTo(i, true);
    });
  }

  step() {
    const first = this.slides[0];
    if (!first) return 1;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  sync() {
    const max = this.slides.length - 1;
    const i = Math.max(0, Math.min(max, Math.round(Math.abs(this.track.scrollLeft) / this.step())));
    // At the end of a two-up track the last slide is fully visible: mark it active.
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    this.setActive(atEnd && this.index === max ? max : i);
  }

  setActive(i) {
    this.index = i;
    this.thumbs.forEach((thumb, n) => {
      if (n === i) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
    const thumb = this.thumbs[i];
    if (thumb && this.rail && this.rail.offsetParent) {
      const top = thumb.offsetTop - this.rail.offsetTop;
      if (top < this.rail.scrollTop || top + thumb.offsetHeight > this.rail.scrollTop + this.rail.clientHeight) {
        this.rail.scrollTo({ top: top - 12, behavior: behavior() });
      }
    }
  }

  goTo(i, animate = true, announce = false) {
    const max = this.slides.length - 1;
    const next = Math.max(0, Math.min(max, i));
    const slide = this.slides[next];
    if (!slide) return;
    const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: dir * next * this.step(), behavior: animate ? behavior() : 'auto' });
    this.setActive(next);
    if (announce && this.status) {
      const template = this.dataset.labelSlide || '';
      this.status.textContent = template.replace('[n]', String(next + 1));
    }
  }

  bindZoom() {
    const dialog = this.dialog;
    if (!dialog) return;
    this.zoomTrack = dialog.querySelector('[data-vb-zoom-track]');
    this.zoomSlides = [...dialog.querySelectorAll('[data-vb-zoom-slide]')];
    this.zoomCount = dialog.querySelector('[data-vb-zoom-count]');
    this.zoomPrev = dialog.querySelector('[data-vb-zoom-prev]');
    this.zoomNext = dialog.querySelector('[data-vb-zoom-next]');
    this.zoomIndex = 0;

    dialog.querySelector('[data-vb-zoom-close]')?.addEventListener('click', () => dialog.close());
    this.zoomPrev?.addEventListener('click', () => this.zoomGo(this.zoomIndex - 1));
    this.zoomNext?.addEventListener('click', () => this.zoomGo(this.zoomIndex + 1));
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.zoomGo(this.zoomIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.zoomGo(this.zoomIndex - 1);
      }
    });
    // Click on the empty area around a contained image closes the lightbox
    dialog.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.matches('.vb-zoom__slide')) dialog.close();
    });
    let frame = 0;
    this.zoomTrack?.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const w = this.zoomTrack.clientWidth || 1;
          this.zoomSetActive(Math.round(Math.abs(this.zoomTrack.scrollLeft) / w));
        });
      },
      { passive: true }
    );
    dialog.addEventListener('close', () => {
      this.zoomReset();
      document.documentElement.classList.remove('vb-zoom-open');
      this.goTo(this.zoomIndex, false);
      this.opener?.focus({ preventScroll: true });
    });
    this.bindDeepZoom();
  }

  /* ---------- Deep zoom inside the lightbox ----------
     + / − buttons (100–500%), the level button resets; double-click or double-tap zooms in on that
     spot (and back out); the mouse wheel zooms around the pointer; two fingers pinch; one finger or
     the mouse drags a zoomed photo. Keys: + / − / 0. While zoomed the track does not swipe, and
     changing photo resets the zoom. The image is moved with a transform (origin top-left). */
  bindDeepZoom() {
    const dialog = this.dialog;
    if (!dialog) return;
    this.zoomLevelEl = dialog.querySelector('[data-vb-zoom-level]');
    this.zoomInBtn = dialog.querySelector('[data-vb-zoom-in]');
    this.zoomOutBtn = dialog.querySelector('[data-vb-zoom-out]');
    this.zState = { s: 1, x: 0, y: 0 };
    this.zMax = 5;
    this.zStep = 1.5;
    const pointers = new Map();
    let pan = null;
    let pinch = null;
    let moved = false;
    let lastTap = { t: 0, x: 0, y: 0 };

    const slideOf = (target) => (target instanceof Element ? target.closest('.vb-zoom__slide') : null);
    const local = (slide, clientX, clientY) => {
      const r = slide.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    };
    const centre = () => {
      const slide = this.zoomSlides[this.zoomIndex];
      return slide ? { x: slide.clientWidth / 2, y: slide.clientHeight / 2 } : { x: 0, y: 0 };
    };

    this.zoomInBtn?.addEventListener('click', () => {
      const c = centre();
      this.zoomAt(this.zState.s * this.zStep, c.x, c.y);
    });
    this.zoomOutBtn?.addEventListener('click', () => {
      const c = centre();
      this.zoomAt(this.zState.s / this.zStep, c.x, c.y);
    });
    dialog.querySelector('[data-vb-zoom-reset]')?.addEventListener('click', () => this.zoomReset());

    dialog.addEventListener('keydown', (event) => {
      if (event.target instanceof HTMLElement && event.target.closest('button') && event.key === '0') return;
      const c = centre();
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.zoomAt(this.zState.s * this.zStep, c.x, c.y);
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        this.zoomAt(this.zState.s / this.zStep, c.x, c.y);
      } else if (event.key === '0') {
        event.preventDefault();
        this.zoomReset();
      }
    });

    this.zoomTrack?.addEventListener(
      'wheel',
      (event) => {
        const slide = slideOf(event.target);
        if (!slide) return;
        event.preventDefault();
        const p = local(slide, event.clientX, event.clientY);
        this.zoomAt(this.zState.s * Math.exp(-event.deltaY * 0.0018), p.x, p.y);
      },
      { passive: false }
    );

    this.zoomTrack?.addEventListener('dblclick', (event) => {
      const slide = slideOf(event.target);
      if (!slide) return;
      event.preventDefault();
      this.zoomToggleAt(slide, event.clientX, event.clientY);
    });

    this.zoomTrack?.addEventListener('pointerdown', (event) => {
      const slide = slideOf(event.target);
      if (!slide) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      moved = false;
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const mid = local(slide, (a.x + b.x) / 2, (a.y + b.y) / 2);
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), s: this.zState.s, mid };
        pan = null;
        slide.setPointerCapture?.(event.pointerId);
      } else if (pointers.size === 1 && this.zState.s > 1) {
        pan = { x: event.clientX, y: event.clientY, ox: this.zState.x, oy: this.zState.y };
        slide.setPointerCapture?.(event.pointerId);
        dialog.setAttribute('data-panning', '');
      }
    });

    this.zoomTrack?.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pinch && pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        this.zoomAt((pinch.s * d) / (pinch.d || 1), pinch.mid.x, pinch.mid.y);
        moved = true;
      } else if (pan) {
        const dx = event.clientX - pan.x;
        const dy = event.clientY - pan.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
        this.zState.x = pan.ox + dx;
        this.zState.y = pan.oy + dy;
        this.zoomApply();
      }
    });

    const end = (event) => {
      if (!pointers.has(event.pointerId)) return;
      const slide = slideOf(event.target) || this.zoomSlides[this.zoomIndex];
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinch = null;
      if (pointers.size === 0) {
        pan = null;
        dialog.removeAttribute('data-panning');
        // Double-tap on touch screens (no dblclick there)
        if (event.pointerType === 'touch' && !moved && slide) {
          const now = Date.now();
          const near = Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) < 30;
          if (now - lastTap.t < 320 && near) {
            this.zoomToggleAt(slide, event.clientX, event.clientY);
            lastTap = { t: 0, x: 0, y: 0 };
          } else {
            lastTap = { t: now, x: event.clientX, y: event.clientY };
          }
        }
      }
    };
    this.zoomTrack?.addEventListener('pointerup', end);
    this.zoomTrack?.addEventListener('pointercancel', end);

    // A drag on a zoomed photo must not count as a click on the backdrop (which closes)
    dialog.addEventListener(
      'click',
      (event) => {
        if ((moved || this.zState.s > 1) && slideOf(event.target)) {
          event.stopImmediatePropagation();
          moved = false;
        }
      },
      true
    );
  }

  zoomImg() {
    return this.zoomSlides?.[this.zoomIndex]?.querySelector('img') || null;
  }

  zoomToggleAt(slide, clientX, clientY) {
    if (this.zState.s > 1.05) {
      this.zoomReset();
      return;
    }
    const r = slide.getBoundingClientRect();
    this.zoomAt(2.5, clientX - r.left, clientY - r.top);
  }

  /** Zoom to `next` keeping the point (cx, cy) of the slide where it is */
  zoomAt(next, cx, cy) {
    const z = this.zState;
    const s = Math.max(1, Math.min(this.zMax, next));
    const k = s / z.s;
    z.x = cx - (cx - z.x) * k;
    z.y = cy - (cy - z.y) * k;
    z.s = s;
    if (s === 1) {
      z.x = 0;
      z.y = 0;
    }
    this.zoomApply();
  }

  zoomReset() {
    if (!this.zState) return;
    this.zState = { s: 1, x: 0, y: 0 };
    this.zoomApply();
  }

  zoomApply() {
    const z = this.zState;
    const img = this.zoomImg();
    const slide = this.zoomSlides?.[this.zoomIndex];
    if (slide) {
      const w = slide.clientWidth;
      const h = slide.clientHeight;
      z.x = Math.min(0, Math.max(w - w * z.s, z.x));
      z.y = Math.min(0, Math.max(h - h * z.s, z.y));
    }
    this.zoomSlides?.forEach((sl) => {
      const im = sl.querySelector('img');
      if (im && im !== img) im.style.transform = '';
    });
    if (img) img.style.transform = z.s > 1 ? `translate(${z.x}px, ${z.y}px) scale(${z.s})` : '';
    this.dialog?.toggleAttribute('data-zoomed', z.s > 1);
    if (this.zoomLevelEl) this.zoomLevelEl.textContent = `${Math.round(z.s * 100)}%`;
    if (this.zoomOutBtn) this.zoomOutBtn.disabled = z.s <= 1;
    if (this.zoomInBtn) this.zoomInBtn.disabled = z.s >= this.zMax;
  }

  openZoom(i) {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;
    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Load the large images only when the lightbox opens
    this.zoomSlides.forEach((slide) => slide.querySelector('img')?.setAttribute('loading', 'eager'));
    dialog.showModal();
    document.documentElement.classList.add('vb-zoom-open');
    this.zoomReset();
    this.zoomGo(i, false);
    dialog.querySelector('[data-vb-zoom-close]')?.focus({ preventScroll: true });
  }

  zoomGo(i, animate = true) {
    this.zoomReset();
    const max = this.zoomSlides.length - 1;
    const next = Math.max(0, Math.min(max, i));
    if (!this.zoomTrack) return;
    const dir = getComputedStyle(this.zoomTrack).direction === 'rtl' ? -1 : 1;
    this.zoomTrack.scrollTo({ left: dir * next * this.zoomTrack.clientWidth, behavior: animate ? behavior() : 'auto' });
    this.zoomSetActive(next);
  }

  zoomSetActive(i) {
    if (this.zState && i !== this.zoomIndex && this.zState.s > 1) {
      this.zoomIndex = i;
      this.zoomReset();
    }
    this.zoomIndex = i;
    const total = this.zoomSlides.length;
    if (this.zoomCount) this.zoomCount.textContent = `${i + 1} / ${total}`;
    if (this.zoomPrev) this.zoomPrev.disabled = i <= 0;
    if (this.zoomNext) this.zoomNext.disabled = i >= total - 1;
  }
}

if (!customElements.get('vb-product-gallery')) customElements.define('vb-product-gallery', VbProductGallery);

/* ---------- Heart: saved on this device ----------
   Handled store-wide by assets/vb-wishlist.js ([data-vb-wish] buttons, loaded by the header). */

/* ---------- Check delivery ----------
   No per-pincode lookup: it checks the Indian pincode format, then shows the store's delivery
   promise (data-msg-ok, built from the block settings), which is the same for every pincode. */
function bindDelivery(root) {
  root.querySelectorAll('form[data-vb-delivery]:not([data-bound])').forEach((form) => {
    form.setAttribute('data-bound', '');
    const input = form.querySelector('input');
    const msg = form.querySelector('[data-vb-delivery-msg]');
    if (!input || !msg) return;
    input.addEventListener('input', () => {
      const digits = input.value.replace(/\D/g, '').slice(0, 6);
      if (digits !== input.value) input.value = digits;
      input.removeAttribute('aria-invalid');
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const pin = input.value.trim();
      const ok = /^[1-9]\d{5}$/.test(pin);
      msg.classList.toggle('is-error', !ok);
      if (ok) {
        input.removeAttribute('aria-invalid');
        msg.textContent = form.dataset.msgOk || '';
      } else {
        input.setAttribute('aria-invalid', 'true');
        msg.textContent = form.dataset.msgInvalid || '';
        input.focus();
      }
    });
  });
}

/* ---------- Sticky ADD TO BAG bar ---------- */
/* Shown once the main buy row has scrolled out of view (above the viewport), hidden while the row
   or the footer is on screen. It has no form of its own: option selects click the main variant
   picker, the qty steps click the main quantity buttons, and ADD TO BAG submits the main form
   (requestSubmit, so Horizon adds the item and opens the cart drawer without the fly animation). */
function bindSticky(root) {
  root.querySelectorAll('[data-vb-buy]:not([data-sticky-bound])').forEach((buy) => {
    const bar = buy.querySelector('[data-vb-sticky]');
    if (!bar) return;
    buy.setAttribute('data-sticky-bound', '');
    const row = buy.querySelector('.vb-buy__row') || buy;
    const form = buy.querySelector('form[data-type="add-to-cart-form"]');
    const add = bar.querySelector('[data-vb-sticky-add]');
    const label = bar.querySelector('[data-vb-sticky-label]');
    const count = bar.querySelector('[data-vb-sticky-qty]');
    const section = buy.closest('.shopify-section') || document;
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    const state = { row: true, rowAbove: false, footer: false };

    const apply = () => {
      const show = !state.row && state.rowAbove && !state.footer && getComputedStyle(bar).display !== 'none';
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      document.documentElement.classList.toggle('vb-sticky-on', show);
    };
    new IntersectionObserver(([entry]) => {
      state.row = entry.isIntersecting;
      state.rowAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      apply();
    }).observe(row);
    if (footer) {
      new IntersectionObserver(([entry]) => {
        state.footer = entry.isIntersecting;
        apply();
      }).observe(footer);
    }
    window.addEventListener('resize', apply, { passive: true });

    // ---- quantity (mirror of the main quantity selector) ----
    const mainQty = () => buy.querySelector('.vb-buy input[name="quantity"]');
    const syncQty = () => {
      const input = mainQty();
      if (count && input) count.textContent = input.value || '1';
    };
    bar.querySelectorAll('[data-vb-sticky-step]').forEach((step) => {
      step.addEventListener('click', () => {
        const dir = Number(step.getAttribute('data-vb-sticky-step'));
        const target = buy.querySelector(dir > 0 ? '.vb-buy .quantity-plus' : '.vb-buy .quantity-minus');
        if (target instanceof HTMLButtonElement && !target.disabled) target.click();
        requestAnimationFrame(syncQty);
      });
    });
    buy.addEventListener('input', syncQty);
    buy.addEventListener('change', syncQty);
    buy.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('.quantity-selector')) requestAnimationFrame(syncQty);
    });

    // ---- options (two-way sync with the main variant picker) ----
    const selects = [...bar.querySelectorAll('[data-vb-sticky-option]')];
    const syncOptions = () => {
      for (const select of selects) {
        const name = select.getAttribute('data-vb-sticky-option');
        const checked = section.querySelector(`variant-picker input[data-option-name="${CSS.escape(name || '')}"]:checked`);
        const dropdown = section.querySelector(`variant-picker select[name^="${CSS.escape(name || '')}"]`);
        const value = checked?.value ?? (dropdown instanceof HTMLSelectElement ? dropdown.value : null);
        if (value != null && select.value !== value) select.value = value;
      }
      if (typeof syncVariantLabel === 'function') syncVariantLabel();
    };
    selects.forEach((select) => {
      select.addEventListener('change', () => {
        const name = select.getAttribute('data-vb-sticky-option') || '';
        const radio = [...section.querySelectorAll(`variant-picker input[data-option-name="${CSS.escape(name)}"]`)].find(
          (input) => input instanceof HTMLInputElement && input.value === select.value
        );
        if (radio instanceof HTMLInputElement) {
          radio.click();
          return;
        }
        const dropdown = section.querySelector(`variant-picker select[name^="${CSS.escape(name)}"]`);
        if (dropdown instanceof HTMLSelectElement) {
          dropdown.value = select.value;
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
    section.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.closest('variant-picker')) syncOptions();
    });
    bar.querySelectorAll('[data-vb-sticky-choose]').forEach((button) =>
      button.addEventListener('click', () => {
        const picker = section.querySelector('variant-picker');
        if (!picker) return;
        picker.scrollIntoView({ behavior: behavior(), block: 'center' });
        const first = picker.querySelector('input:checked, input, select');
        if (first instanceof HTMLElement) first.focus({ preventScroll: true });
      })
    );
    const variantLabel = bar.querySelector('[data-vb-sticky-variant]');
    const syncVariantLabel = () => {
      if (variantLabel && selects.length) variantLabel.textContent = selects.map((select) => select.value).join(' / ');
    };

    // ---- ADD TO BAG: submit the main form ----
    add?.addEventListener('click', () => {
      const main = buy.querySelector('.vb-buy__add-btn');
      if (!(form instanceof HTMLFormElement) || (main instanceof HTMLButtonElement && main.disabled)) {
        row.scrollIntoView({ behavior: behavior(), block: 'center' });
        return;
      }
      form.requestSubmit();
    });

    // ---- variant changes: price, image, availability, options ----
    onVariantChange(({ html, resource }) => {
      const price = bar.querySelector('[data-vb-sticky-price]');
      const next = html?.querySelector?.('[data-vb-sticky-price]');
      if (price && next) price.textContent = next.textContent;
      const img = bar.querySelector('[data-vb-sticky-img]');
      const nextImg = html?.querySelector?.('[data-vb-sticky-img]');
      if (img && nextImg) img.replaceWith(nextImg.cloneNode(true));
      const available = Boolean(resource && resource.available !== false);
      if (add instanceof HTMLButtonElement) add.disabled = !available;
      if (label) {
        label.textContent = (available ? bar.dataset.labelAdd : resource ? bar.dataset.labelSoldout : bar.dataset.labelUnavailable) || '';
      }
      add?.querySelector('.vb-btn__icon')?.toggleAttribute('hidden', !available);
      syncOptions();
      syncVariantLabel();
      syncQty();
    });
  });
}

/* ---------- You may also love: Shopify recommendations ---------- */
function bindRecs(root) {
  root.querySelectorAll('[data-vb-recs][data-url]:not([data-vb-recs-ready]):not([data-bound])').forEach((section) => {
    section.setAttribute('data-bound', '');
    const load = () => {
      fetch(section.getAttribute('data-url') || '')
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => {
          if (!text) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.querySelector('[data-vb-recs][data-vb-recs-ready] [data-vb-recs-grid]');
          const grid = section.querySelector('[data-vb-recs-grid]');
          // Shopify answers with a generic collection fallback when it has no related data for this
          // product (same items for every product): keep the server's charm/type-based list then.
          const links = fresh ? [...fresh.querySelectorAll('a[href]')] : [];
          const onlyFallback = links.length > 0 && links.every((a) => /pr_prod_strat=collection_fallback/.test(a.getAttribute('href') || ''));
          if (fresh && grid && fresh.children.length > 0 && !onlyFallback) {
            grid.replaceChildren(...fresh.children);
            section.setAttribute('data-vb-recs-ready', '');
          }
        })
        .catch(() => {
          /* keep the fallback collection */
        });
    };
    if (!('IntersectionObserver' in window)) return load();
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          io.disconnect();
          load();
        }
      },
      { rootMargin: '800px 0px' }
    );
    io.observe(section);
  });
}

function init(root = document) {
  bindDelivery(root);
  bindSticky(root);
  bindRecs(root);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();

// Theme editor: re-bind when a section is re-rendered
document.addEventListener('shopify:section:load', (event) => init(event.target instanceof Element ? event.target : document));
